// Tab Tổng quan: KPI so với kỳ trước, xu hướng ban hành, cơ cấu trạng thái, việc cần xử lý, tỷ lệ đọc theo phòng ban,
// thời gian duyệt, dung lượng lưu trữ, hoạt động gần đây. Mọi khối bấm được để mở danh sách đã lọc.
import { useState } from 'react';
import { Modal, Table, Tooltip } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { Bar, CartesianGrid, Cell, ComposedChart, Line, LineChart, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle, BookOpenCheck, CalendarClock, ChevronRight, EyeOff, FileCheck2, FileClock, FileText, Hourglass, MessageSquareText, Undo2,
} from 'lucide-react';
import type { Employee } from '../data';
import { fmtSize } from '../logic';
import { EXPIRING_DAYS, FLAG_LABEL, LOWREAD_RATE, OVERDUE_DAYS, SLA_DAYS, STORAGE_QUOTA, relTime } from '../stats';
import type { overviewAnalytics } from '../analytics';
import { BRAND, docLink } from '../utils';
import { Card, Delta, type DashCtx } from './ui';

const STATUS_SLICES = [
  { key: 'active', label: 'Còn hiệu lực', color: '#1D9E75', tab: 'active' },
  { key: 'upcoming', label: 'Chưa hiệu lực', color: '#378ADD', tab: 'upcoming' },
  { key: 'expired', label: 'Hết hiệu lực / thay thế', color: '#888780', tab: 'expired' },
  { key: 'revoked', label: 'Thu hồi', color: '#E24B4A', tab: 'revoked' },
  { key: 'working', label: 'Dự thảo / chờ duyệt', color: '#EF9F27', tab: 'pending' },
] as const;
const EXT_COLOR: Record<string, string> = { pdf: '#E24B4A', doc: '#378ADD', img: '#EF9F27', ppt: '#D85A30', xls: '#639922', zip: '#888780' };


export default function OverviewTab({ ctx, data }: { ctx: DashCtx; data: ReturnType<typeof overviewAnalytics> }) {
  const navigate = useNavigate();
  const { people, goList } = ctx;
  const [deptModal, setDeptModal] = useState<string | null>(null);
  const totalStates = Object.values(data.stateCount).reduce((s, x) => s + x, 0);
  const prevLabel = 'so với kỳ trước';

  const kpis = [
    { label: 'Đang hiệu lực', value: data.active, icon: <FileCheck2 size={18} />, tone: 'text-emerald-600 bg-emerald-50', delta: <Delta cur={data.active} prev={data.activePrev} kind="count" suffix={prevLabel} />, onClick: () => goList({ tab: 'active' }) },
    { label: 'Ban hành mới', value: data.issued, icon: <FileText size={18} />, tone: 'text-sky-600 bg-sky-50', delta: <Delta cur={data.issued} prev={data.issuedPrev} kind="percent" suffix={prevLabel} />, onClick: () => goList({}) },
    {
      label: 'Chờ duyệt', value: data.pending, icon: <Hourglass size={18} />, tone: 'text-amber-600 bg-amber-50', onClick: () => navigate('/document-approval'),
      delta: data.overdue.length ? <span className="text-xs font-medium text-red-500">{data.overdue.length} quá hạn {OVERDUE_DAYS} ngày</span> : <span className="text-xs text-slate-400">Không có hồ sơ quá hạn</span>,
    },
    { label: 'Tỷ lệ đã đọc', value: `${Math.round(data.readNow * 100)}%`, icon: <BookOpenCheck size={18} />, tone: 'text-violet-600 bg-violet-50', delta: <Delta cur={data.readNow} prev={data.readPrev} kind="points" suffix={prevLabel} />, onClick: () => document.getElementById('dept-read')?.scrollIntoView({ behavior: 'smooth' }) },
    {
      label: 'Sắp hết hiệu lực', value: data.expiring.length, icon: <CalendarClock size={18} />, tone: 'text-orange-600 bg-orange-50', onClick: () => goList({ flag: 'expiring' }),
      delta: <span className="text-xs text-slate-400">trong {EXPIRING_DAYS} ngày tới</span>,
    },
  ];

  const actions: { key: string; icon: React.ReactNode; label: string; count: number; tone: string; onClick: () => void }[] = [
    { key: 'overdue', icon: <Hourglass size={17} />, label: FLAG_LABEL.overdue, count: data.overdue.length, tone: 'red', onClick: () => goList({ tab: 'pending', flag: 'overdue' }) },
    { key: 'expiring', icon: <CalendarClock size={17} />, label: `${FLAG_LABEL.expiring} — cần gia hạn hoặc ban hành lại`, count: data.expiring.length, tone: 'amber', onClick: () => goList({ flag: 'expiring' }) },
    { key: 'lowread', icon: <EyeOff size={17} />, label: FLAG_LABEL.lowread, count: data.lowread.length, tone: 'amber', onClick: () => goList({ flag: 'lowread' }) },
    { key: 'comments', icon: <MessageSquareText size={17} />, label: FLAG_LABEL.comments, count: data.comments.length, tone: 'sky', onClick: () => goList({ tab: 'draft', flag: 'comments' }) },
    { key: 'rejected', icon: <Undo2 size={17} />, label: 'Bị từ chối, chưa gửi lại', count: data.rejected.length, tone: 'slate', onClick: () => goList({ tab: 'rejected' }) },
  ];
  const TONE: Record<string, { icon: string; badge: string }> = {
    red: { icon: 'text-red-500', badge: 'bg-red-50 text-red-600' },
    amber: { icon: 'text-amber-500', badge: 'bg-amber-50 text-amber-700' },
    sky: { icon: 'text-sky-500', badge: 'bg-sky-50 text-sky-700' },
    slate: { icon: 'text-slate-400', badge: 'bg-slate-100 text-slate-600' },
  };

  const deptDetail = data.depts.find(x => x.id === deptModal);
  const deptRows = deptDetail
    ? Array.from(deptDetail.unread.entries()).map(([pid, list]) => ({ person: people.find(x => x.id === pid) as Employee, docs: list })).sort((a, b) => b.docs.length - a.docs.length)
    : [];


  return (
    <>
      {/* KPI */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {kpis.map(k => (
          <button key={k.label} onClick={k.onClick} className="group rounded-xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:border-[#3e91ab]/40 hover:shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate-500">{k.label}</span>
              <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${k.tone}`}>{k.icon}</span>
            </div>
            <div className="mt-1 text-[26px] font-bold leading-tight text-slate-800">{k.value}</div>
            <div className="mt-1 min-h-[18px]">{k.delta}</div>
          </button>
        ))}
      </div>

      {/* Xu hướng + cơ cấu */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Ban hành và thu hồi theo tháng" hint="12 tháng gần nhất" className="lg:col-span-2">
          <div className="mb-2 flex flex-wrap gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: BRAND.primary }} />Ban hành (cột)</span>
            <span className="flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed border-red-500" />Thu hồi (nét đứt)</span>
            <span className="flex items-center gap-1.5"><span className="w-4 border-t-2 border-slate-400" />Cùng kỳ năm trước</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.monthly} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={0} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <ChartTooltip
                  labelFormatter={(_l, p) => `Tháng ${p?.[0]?.payload?.full ?? ''}`}
                  formatter={(v, n) => [`${v} tài liệu`, n === 'issued' ? 'Ban hành' : n === 'revoked' ? 'Thu hồi' : 'Cùng kỳ năm trước']}
                />
                <Bar dataKey="issued" fill={BRAND.primary} radius={[4, 4, 0, 0]} barSize={22} />
                <Line dataKey="lastYear" stroke="#94a3b8" strokeWidth={1.5} dot={false} type="linear" />
                <Line dataKey="revoked" stroke="#E24B4A" strokeDasharray="5 4" strokeWidth={2} dot={{ r: 2 }} type="linear" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Cơ cấu trạng thái" hint="bấm để lọc">
          <div className="relative h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={STATUS_SLICES.map(s => ({ ...s, value: data.stateCount[s.key] }))} dataKey="value" nameKey="label" innerRadius="66%" outerRadius="95%" paddingAngle={1.5} stroke="none"
                  onClick={(e: any) => goList({ tab: e?.payload?.tab ?? e?.tab })} className="cursor-pointer"
                >
                  {STATUS_SLICES.map(s => <Cell key={s.key} fill={s.color} />)}
                </Pie>
                <ChartTooltip formatter={(v, n) => [`${v} tài liệu`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-slate-800">{totalStates}</span>
              <span className="text-xs text-slate-400">tài liệu</span>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-1.5 text-[13px] sm:grid-cols-2 lg:grid-cols-1">
            {STATUS_SLICES.map(s => (
              <button key={s.key} onClick={() => goList({ tab: s.tab })} className="flex items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-50">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
                <span className="flex-1 truncate text-slate-600">{s.label}</span>
                <span className="font-medium text-slate-800">{data.stateCount[s.key]}</span>
                <span className="w-9 text-right text-xs text-slate-400">{totalStates ? Math.round((data.stateCount[s.key] / totalStates) * 100) : 0}%</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Cần xử lý + tỷ lệ đọc */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={<span className="flex items-center gap-2"><AlertTriangle size={16} className="text-amber-500" />Cần xử lý</span>} hint="bấm để mở danh sách đã lọc">
          <div className="divide-y divide-slate-100">
            {actions.map(a => (
              <button key={a.key} onClick={a.onClick} className="flex w-full items-center gap-3 px-1 py-2.5 text-left text-[13px] hover:bg-slate-50">
                <span className={TONE[a.tone].icon}>{a.icon}</span>
                <span className={`flex-1 ${a.count ? 'text-slate-700' : 'text-slate-400'}`}>{a.label}</span>
                <span className={`min-w-[28px] rounded-md px-2 py-0.5 text-center text-xs font-semibold ${a.count ? TONE[a.tone].badge : 'bg-slate-50 text-slate-300'}`}>{a.count}</span>
                <ChevronRight size={15} className="text-slate-300" />
              </button>
            ))}
          </div>
        </Card>
        <Card title={<span id="dept-read">Tỷ lệ đã đọc theo phòng ban</span>} hint="bấm để xem ai chưa đọc">
          {data.depts.length === 0 ? <div className="py-8 text-center text-sm text-slate-400">Chưa có tài liệu ban hành</div> : (
            <div className="space-y-2.5">
              {data.depts.map(x => {
                const pct = Math.round(x.rate * 100);
                const color = x.rate >= 0.7 ? '#1D9E75' : x.rate >= LOWREAD_RATE ? '#EF9F27' : '#E24B4A';
                return (
                  <Tooltip key={x.id} title={`${x.read}/${x.total} lượt đã đọc · ${x.unread.size} người còn tài liệu chưa đọc`}>
                    <button onClick={() => setDeptModal(x.id)} className="grid w-full grid-cols-[130px_1fr_44px] items-center gap-3 rounded px-1 py-0.5 text-left text-[13px] hover:bg-slate-50">
                      <span className="truncate text-slate-600">{x.name}</span>
                      <span className="h-2 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></span>
                      <span className="text-right font-medium text-slate-700">{pct}%</span>
                    </button>
                  </Tooltip>
                );
              })}
              <div className="flex gap-4 pt-1 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#1D9E75]" />≥ 70%</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#EF9F27]" />50–69%</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#E24B4A]" />&lt; 50%</span>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Thời gian duyệt + lưu trữ + hoạt động */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card title="Thời gian duyệt TB" hint={`mục tiêu ≤ ${SLA_DAYS} ngày`}>
          {data.slaCount ? (
            <>
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="text-[26px] font-bold text-slate-800">{data.slaAvg.toFixed(1).replace('.', ',')} ngày</span>
                <Delta cur={Math.round(data.slaAvg * 10) / 10} prev={Math.round(data.slaPrevAvg * 10) / 10} kind="days" goodWhenUp={false} />
              </div>
              <div className="text-xs text-slate-400">{data.slaCount} tài liệu duyệt trong kỳ · {Math.round(data.slaWithin * 100)}% đạt mục tiêu</div>
            </>
          ) : <div className="text-sm text-slate-400">Chưa có tài liệu được duyệt trong kỳ</div>}
          <div className="mt-3 h-32">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.slaTrend} margin={{ top: 8, right: 8, left: -28, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false}
                  domain={[0, (max: number) => Math.max(SLA_DAYS + 1, Math.ceil(max))]} />
                <ReferenceLine y={SLA_DAYS} stroke="#EF9F27" strokeDasharray="4 4" label={{ value: 'mục tiêu', position: 'insideTopRight', fontSize: 10, fill: '#BA7517' }} />
                <ChartTooltip formatter={v => [`${v} ngày`, 'Trung bình']} />
                <Line dataKey="days" stroke={BRAND.primary} strokeWidth={2} dot={{ r: 2.5 }} connectNulls type="monotone" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Dung lượng lưu trữ" hint="gồm cả thùng rác">
          <div className="flex items-baseline gap-2">
            <span className="text-[26px] font-bold text-slate-800">{fmtSize(data.used)}</span>
            <span className="text-xs text-slate-400">/ {fmtSize(STORAGE_QUOTA)} ({Math.max(data.used ? 1 : 0, Math.round((data.used / STORAGE_QUOTA) * 100))}%)</span>
          </div>
          <div className="my-3 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
            {data.storage.map(s => <span key={s.key} style={{ width: `${(s.size / STORAGE_QUOTA) * 100}%`, background: EXT_COLOR[s.key], minWidth: s.size ? 2 : 0 }} />)}
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-[13px]">
            {data.storage.map(s => (
              <button key={s.key} onClick={() => goList({ ext: s.key })} className="flex items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-50">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: EXT_COLOR[s.key] }} />
                <span className="flex-1 text-slate-600">{s.label}</span>
                <span className="text-slate-500">{fmtSize(s.size)}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card title="Hoạt động gần đây" className="md:col-span-2 lg:col-span-1">
          {data.activity.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Chưa có hoạt động</div> : (
            <ul className="divide-y divide-slate-100">
              {data.activity.map((a, i) => {
                const color = /Từ chối|Thu hồi|thùng rác/.test(a.action) ? 'bg-red-500' : /ban hành/i.test(a.action) ? 'bg-emerald-500' : /Gửi duyệt/.test(a.action) ? 'bg-amber-500' : 'bg-sky-500';
                return (
                  <li key={`${a.doc.id}-${i}`} className="flex gap-2.5 py-2 text-[13px]">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${color}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-700"><b className="font-medium">{a.by}</b> {a.action.charAt(0).toLowerCase() + a.action.slice(1)}</div>
                      <Link to={docLink(a.doc.id)} className="block truncate text-xs text-[#357f96] hover:underline">{a.doc.code} — {a.doc.title}</Link>
                    </div>
                    <span className="shrink-0 text-[11px] text-slate-400">{relTime(a.at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <Modal
        open={!!deptDetail} onCancel={() => setDeptModal(null)} footer={null} width={720} destroyOnHidden
        title={deptDetail && <span>{deptDetail.name} · <span className="font-normal text-slate-500">đã đọc {Math.round(deptDetail.rate * 100)}% ({deptDetail.read}/{deptDetail.total} lượt)</span></span>}
      >
        <Table
          size="small" rowKey={r => r.person.id} dataSource={deptRows} pagination={{ pageSize: 8, hideOnSinglePage: true }}
          locale={{ emptyText: 'Mọi người trong phòng đã đọc hết tài liệu được chia sẻ' }}
          columns={[
            { title: 'Nhân sự', key: 'name', width: 200, render: (_v, r) => <div><div className="font-medium text-slate-700">{r.person.name}</div><div className="text-[11px] text-slate-400">{r.person.title}</div></div> },
            { title: 'Chưa đọc', key: 'n', width: 90, align: 'center', render: (_v, r) => <span className="font-semibold text-red-500">{r.docs.length}</span> },
            {
              title: 'Tài liệu chưa đọc', key: 'docs',
              render: (_v, r) => (
                <div className="flex flex-wrap gap-1">
                  {r.docs.slice(0, 4).map(d => <Link key={d.id} to={docLink(d.id)} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-[#357f96] hover:underline">{d.code}</Link>)}
                  {r.docs.length > 4 && <span className="text-[11px] text-slate-400">+{r.docs.length - 4}</span>}
                </div>
              ),
            },
          ]}
        />
        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><FileClock size={13} /> Chỉ tính tài liệu đã ban hành / đã thu hồi được chia sẻ cho từng người.</div>
      </Modal>
    </>
  );
}
